import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from './config/api';
import './IntakeFormViewer.css';

function IntakeFormViewer({ user, onBack, inline = false }) {
  const [intakeData, setIntakeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchIntakeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchIntakeData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.INTAKE, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIntakeData(data.intakeData);
      } else if (response.status === 404) {
        setIntakeData(null);
      } else {
        setError('Failed to load intake form data');
      }
    } catch (error) {
      console.error('Error fetching intake data:', error);
      setError('Failed to load intake form data');
    } finally {
      setLoading(false);
    }
  };

  const formatAssessmentStatus = (status) => {
    const statusMap = {
      'never': 'Never taken',
      'recent': 'Taken in last 18 months',
      'old': 'Taken longer than 18 months ago',
      'unsure': 'Unsure'
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const handleEditClick = () => {
    setEditedData({...intakeData});
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedData(null);
    setIsEditing(false);
  };

  const handleFieldChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEditedData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setEditedData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(API_ENDPOINTS.INTAKE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(editedData)
      });

      if (response.ok) {
        const data = await response.json();
        setIntakeData(data.intakeData);
        setIsEditing(false);
        setEditedData(null);
        alert('Your profile has been updated successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to update profile: ${errorData.message || 'Please try again'}`);
      }
    } catch (error) {
      console.error('Error updating intake data:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`profile-page ${inline ? 'profile-inline' : ''}`}>
        {!inline && (
          <header className="navbar">
            <div className="nav-brand">
              <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">
                <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="nav-logo" />
              </a>
            </div>
            <nav className="nav-links">
              {onBack && <button className="back-btn" onClick={onBack}>← Back to Dashboard</button>}
            </nav>
          </header>
        )}
        <main className="main-content">
          <div className="loading-state">Loading your profile...</div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`profile-page ${inline ? 'profile-inline' : ''}`}>
        {!inline && (
          <header className="navbar">
            <div className="nav-brand">
              <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">
                <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="nav-logo" />
              </a>
            </div>
            <nav className="nav-links">
              {onBack && <button className="back-btn" onClick={onBack}>← Back to Dashboard</button>}
            </nav>
          </header>
        )}
        <main className="main-content">
          <div className="error-state">{error}</div>
        </main>
      </div>
    );
  }

  if (!intakeData) {
    return (
      <div className={`profile-page ${inline ? 'profile-inline' : ''}`}>
        {!inline && (
          <header className="navbar">
            <div className="nav-brand">
              <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">
                <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="nav-logo" />
              </a>
            </div>
            <nav className="nav-links">
              {onBack && <button className="back-btn" onClick={onBack}>← Back to Dashboard</button>}
            </nav>
          </header>
        )}
        <main className="main-content">
          <div className="empty-state">
            <p>You haven't completed the intake form yet.</p>
            <p>The intake form will be shown automatically on your next login.</p>
          </div>
        </main>
      </div>
    );
  }

  const displayData = isEditing ? editedData : intakeData;

  return (
    <div className={`profile-page ${inline ? 'profile-inline' : ''}`}>
      {!inline && (
        <header className="navbar">
          <div className="nav-brand">
            <a href="https://www.nextleadercoaching.com" target="_blank" rel="noopener noreferrer">
              <img src="/NextLeaderLogo_Web.png" alt="NextLeader Coaching" className="nav-logo" />
            </a>
          </div>
          <nav className="nav-links">
            {onBack && <button className="back-btn" onClick={onBack}>← Back to Dashboard</button>}
            <div className="header-actions">
              {!isEditing ? (
                <button className="edit-profile-btn" onClick={handleEditClick}>
                  Edit Profile
                </button>
              ) : (
                <>
                  <button className="save-profile-btn" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button className="cancel-profile-btn" onClick={handleCancelEdit} disabled={saving}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          </nav>
        </header>
      )}
      <main className="main-content profile-main">
        <div className="profile-header">
          <h2>My Profile</h2>
          {inline && (
            <div className="inline-actions">
              {!isEditing ? (
                <button className="edit-profile-btn" onClick={handleEditClick}>
                  Edit Profile
                </button>
              ) : (
                <>
                  <button className="save-profile-btn" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button className="cancel-profile-btn" onClick={handleCancelEdit} disabled={saving}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          )}
          <div className="intake-meta">
            <span>Submitted: {formatDate(intakeData.submittedAt)}</span>
            {intakeData.lastUpdated && intakeData.lastUpdated !== intakeData.submittedAt && (
              <span> • Last Updated: {formatDate(intakeData.lastUpdated)}</span>
            )}
          </div>
        </div>

      <div className="intake-sections">
        {/* Personal Information */}
        <div className="intake-section">
          <h2>Personal Information</h2>
          <div className="intake-content">
            <div className="form-row">
              <div className="field-group">
                <label>Preferred Name:</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={displayData.preferredName || ''}
                    onChange={(e) => handleFieldChange('preferredName', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  <span>{displayData.preferredName || 'Not provided'}</span>
                )}
              </div>
              <div className="field-group">
                <label>Last Name:</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={displayData.lastName || ''}
                    onChange={(e) => handleFieldChange('lastName', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  <span>{displayData.lastName || 'Not provided'}</span>
                )}
              </div>
            </div>
            <div className="form-row">
              <div className="field-group">
                <label>Phone:</label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={displayData.phone || ''}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  <span>{displayData.phone || 'Not provided'}</span>
                )}
              </div>
              <div className="field-group">
                <label>Date of Birth:</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={formatDateForInput(displayData.dateOfBirth)}
                    onChange={(e) => handleFieldChange('dateOfBirth', e.target.value)}
                    className="edit-input"
                  />
                ) : (
                  <span>{formatDate(displayData.dateOfBirth)}</span>
                )}
              </div>
            </div>

            <div className="field-group">
              <label>Mailing Address:</label>
              {isEditing ? (
                <div className="address-edit-block">
                  <input
                    type="text"
                    value={displayData.address || ''}
                    onChange={(e) => handleFieldChange('address', e.target.value)}
                    placeholder="Street Address"
                    className="edit-input"
                  />
                  <div className="form-row">
                    <input
                      type="text"
                      value={displayData.city || ''}
                      onChange={(e) => handleFieldChange('city', e.target.value)}
                      placeholder="City"
                      className="edit-input"
                    />
                    <input
                      type="text"
                      value={displayData.state || ''}
                      onChange={(e) => handleFieldChange('state', e.target.value)}
                      placeholder="State"
                      className="edit-input"
                    />
                  </div>
                  <div className="form-row">
                    <input
                      type="text"
                      value={displayData.zipCode || ''}
                      onChange={(e) => handleFieldChange('zipCode', e.target.value)}
                      placeholder="ZIP Code"
                      className="edit-input"
                    />
                    <input
                      type="text"
                      value={displayData.country || ''}
                      onChange={(e) => handleFieldChange('country', e.target.value)}
                      placeholder="Country"
                      className="edit-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="address-block">
                  <div>{displayData.address || 'Not provided'}</div>
                  {(displayData.city || displayData.state || displayData.zipCode) && (
                    <div>
                      {[displayData.city, displayData.state, displayData.zipCode].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {displayData.country && displayData.country !== 'United States' && (
                    <div>{displayData.country}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Personal Achievements */}
        <div className="intake-section">
          <h2>Personal Achievements</h2>
          <div className="intake-content">
            <div className="field-group">
              <label>Greatest Achievement:</label>
              {isEditing ? (
                <textarea
                  value={displayData.greatestAchievement || ''}
                  onChange={(e) => handleFieldChange('greatestAchievement', e.target.value)}
                  className="edit-textarea"
                  rows="4"
                />
              ) : (
                <p>{displayData.greatestAchievement || 'Not provided'}</p>
              )}
            </div>
            <div className="field-group">
              <label>Next Major Milestone:</label>
              {isEditing ? (
                <textarea
                  value={displayData.nextMilestone || ''}
                  onChange={(e) => handleFieldChange('nextMilestone', e.target.value)}
                  className="edit-textarea"
                  rows="4"
                />
              ) : (
                <p>{displayData.nextMilestone || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Self-Assessments */}
        <div className="intake-section">
          <h2>Self-Assessments</h2>
          <div className="intake-content">
            <div className="assessments-grid">
              {displayData.assessments && Object.entries(displayData.assessments).map(([key, status]) => {
                const labels = {
                  'insightsDiscovery': 'Insights Discovery',
                  'cliftonStrengths': 'Clifton Strengths',
                  'myersBriggs': 'Myers-Briggs',
                  'leadershipCircle': 'Leadership Circle Profile',
                  'discProfile': 'DISC Profile',
                  'other': 'Other'
                };

                return (
                  <div key={key} className="assessment-item">
                    <div className="assessment-name">
                      {key === 'other' && displayData.otherAssessmentName
                        ? displayData.otherAssessmentName
                        : labels[key] || key
                      }
                    </div>
                    {isEditing ? (
                      <select
                        value={status}
                        onChange={(e) => handleFieldChange(`assessments.${key}`, e.target.value)}
                        className="edit-select"
                      >
                        <option value="never">Never taken</option>
                        <option value="recent">Taken in last 18 months</option>
                        <option value="old">Taken longer than 18 months ago</option>
                        <option value="unsure">Unsure</option>
                      </select>
                    ) : (
                      <div className="assessment-status">{formatAssessmentStatus(status)}</div>
                    )}
                    {status !== 'never' && (
                      <div className="assessment-results">
                        <label>Results/Details:</label>
                        {isEditing ? (
                          <textarea
                            value={displayData.assessmentResults?.[key] || ''}
                            onChange={(e) => handleFieldChange(`assessmentResults.${key}`, e.target.value)}
                            className="edit-textarea"
                            rows="3"
                            placeholder="Enter assessment results or details..."
                          />
                        ) : (
                          <p>{displayData.assessmentResults?.[key] || 'Not provided'}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Coaching Goals */}
        <div className="intake-section">
          <h2>Coaching Goals</h2>
          <div className="intake-content">
            <div className="field-group">
              <label>Previous Coaching Experience:</label>
              {isEditing ? (
                <>
                  <select
                    value={displayData.previousCoaching ? 'yes' : 'no'}
                    onChange={(e) => handleFieldChange('previousCoaching', e.target.value === 'yes')}
                    className="edit-select"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  {displayData.previousCoaching && (
                    <div className="sub-field">
                      <label>Previous Experience Details:</label>
                      <textarea
                        value={displayData.previousCoachingDescription || ''}
                        onChange={(e) => handleFieldChange('previousCoachingDescription', e.target.value)}
                        className="edit-textarea"
                        rows="3"
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <span>{displayData.previousCoaching ? 'Yes' : 'No'}</span>
                  {displayData.previousCoaching && displayData.previousCoachingDescription && (
                    <div className="sub-field">
                      <label>Previous Experience Details:</label>
                      <p>{displayData.previousCoachingDescription}</p>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="field-group">
              <label>Why Seeking a Coach:</label>
              {isEditing ? (
                <textarea
                  value={displayData.whySeekingCoach || ''}
                  onChange={(e) => handleFieldChange('whySeekingCoach', e.target.value)}
                  className="edit-textarea"
                  rows="4"
                />
              ) : (
                <p>{displayData.whySeekingCoach || 'Not provided'}</p>
              )}
            </div>
            <div className="field-group">
              <label>Specific Goals:</label>
              {isEditing ? (
                <textarea
                  value={displayData.specificGoals || ''}
                  onChange={(e) => handleFieldChange('specificGoals', e.target.value)}
                  className="edit-textarea"
                  rows="4"
                />
              ) : (
                <p>{displayData.specificGoals || 'Not provided'}</p>
              )}
            </div>
            <div className="field-group">
              <label>Current Challenges:</label>
              {isEditing ? (
                <textarea
                  value={displayData.currentChallenges || ''}
                  onChange={(e) => handleFieldChange('currentChallenges', e.target.value)}
                  className="edit-textarea"
                  rows="4"
                />
              ) : (
                <p>{displayData.currentChallenges || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="intake-section">
          <h2>Additional Information</h2>
          <div className="intake-content">
            <div className="field-group">
              <label>Additional Info:</label>
              {isEditing ? (
                <textarea
                  value={displayData.additionalInfo || ''}
                  onChange={(e) => handleFieldChange('additionalInfo', e.target.value)}
                  className="edit-textarea"
                  rows="4"
                />
              ) : (
                <p>{displayData.additionalInfo || 'Not provided'}</p>
              )}
            </div>
            <div className="field-group">
              <label>Coach Expectations:</label>
              {isEditing ? (
                <textarea
                  value={displayData.coachExpectations || ''}
                  onChange={(e) => handleFieldChange('coachExpectations', e.target.value)}
                  className="edit-textarea"
                  rows="4"
                />
              ) : (
                <p>{displayData.coachExpectations || 'Not provided'}</p>
              )}
            </div>
            <div className="field-group">
              <label>Hobbies & Interests:</label>
              {isEditing ? (
                <textarea
                  value={displayData.hobbies || ''}
                  onChange={(e) => handleFieldChange('hobbies', e.target.value)}
                  className="edit-textarea"
                  rows="4"
                />
              ) : (
                <p>{displayData.hobbies || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>
      </div>
      </main>
    </div>
  );
}

export default IntakeFormViewer;
